import { html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import { pb } from "../api/client.js";
import { humanError } from "../api/errors.js";
import { search, type ScopeRecord, type SearchItem } from "../api/registry.js";
import { myRoleInTeam } from "../api/teams.js";
import { roleAtLeast, type Role } from "../lib/roles.js";
import { previewType } from "../lib/preview.js";
import { downloadIcon } from "../lib/icons.js";
import { formatCount } from "../lib/format.js";
import "../components/ibcs-preview.js";

/**
 * page-scope-deleted lists the archived ("deleted") packages for a scope —
 * maintainer/owner only (the backend's search endpoint enforces the same
 * bar independently, so this is a UI convenience, not the real gate).
 * Restoring a package happens from its own Settings tab (page-package.ts);
 * this page is purely a finder, since an archived package is otherwise only
 * reachable by already knowing its exact @scope/name.
 */
@customElement("page-scope-deleted")
export class PageScopeDeleted extends PageElement {
  static override styles = [
    shared,
    css`
      .frame {
        border: 1px solid var(--border);
        border-radius: 16px;
        overflow: hidden;
        background: var(--surface);
        margin-bottom: var(--sp-5);
      }
      .head {
        padding: 24px 28px;
        border-bottom: 1px solid var(--border);
      }
      .back {
        display: inline-block;
        font: 500 12.5px/1 var(--sans);
        color: var(--text-dim);
        margin-bottom: 12px;
      }
      .head h1 {
        font: 600 22px/1 var(--mono);
        color: var(--text);
        margin: 0 0 6px;
      }
      .head p {
        font: 400 14px/1.5 var(--sans);
        color: var(--text-dim);
        margin: 0;
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
        padding: 20px 28px 28px;
      }
      @media (max-width: 900px) {
        .grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      @media (max-width: 600px) {
        .grid {
          grid-template-columns: 1fr;
        }
      }
      .pcard {
        border: 1px solid var(--border);
        border-radius: 11px;
        background: var(--surface);
        overflow: hidden;
        display: block;
      }
      .pcard:hover {
        text-decoration: none;
        border-color: var(--brand);
      }
      .pcard .well {
        height: 104px;
        border-bottom: 1px solid var(--border);
        background: var(--surface-2);
        padding: 11px 13px;
      }
      .pcard .body {
        padding: 13px 15px 15px;
      }
      .pcard .name {
        font: 600 13.5px/1.2 var(--mono);
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 7px;
      }
      .pcard .desc {
        font: 400 12.5px/1.45 var(--sans);
        color: var(--text-dim);
        margin: 0 0 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .pcard .crow {
        display: flex;
        align-items: center;
        gap: 10px;
        font: 500 11.5px/1 var(--mono);
        color: var(--text-faint);
      }
      .pcard .crow .v {
        color: var(--text-dim);
      }
      .pcard .crow .k {
        margin-left: auto;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .empty {
        padding: 40px 28px;
        color: var(--text-dim);
      }
    `,
  ];

  @property() scope = "";

  @state() private myRole: Role | null | undefined = undefined;
  @state() private packages: SearchItem[] = [];
  @state() private errorMsg = "";

  override willUpdate(changed: PropertyValues) {
    if (changed.has("scope")) void this.load();
  }

  private async load() {
    this.setTitle(this.scope ? `Deleted packages · @${this.scope}` : "Deleted packages");
    this.errorMsg = "";
    this.myRole = undefined;
    this.packages = [];
    if (!this.scope) {
      this.myRole = null;
      return;
    }
    try {
      const record = await pb
        .collection("scopes")
        .getFirstListItem<ScopeRecord>(pb.filter("name = {:n}", { n: this.scope }), {
          expand: "team",
        })
        .catch(() => null as unknown as ScopeRecord);
      const role = record?.team ? await myRoleInTeam(record.team).catch(() => null) : null;
      if (roleAtLeast(role, "maintainer")) {
        const res = await search({ scope: [this.scope], archived: true, perPage: 100, facets: false });
        this.packages = res.items;
      }
      this.myRole = role;
    } catch (e) {
      this.errorMsg = humanError(e);
      this.myRole = null;
    }
  }

  private renderGrid() {
    if (!this.packages.length) {
      return html`<div class="empty">No deleted packages in this scope.</div>`;
    }
    return html`<div class="grid">
      ${this.packages.map((p) => {
        const coord = p.scope.replace(/^@/, "");
        const desc = p.title || p.description || "";
        return html`<a
          class="pcard"
          href=${`/p/${coord}/${p.name}`}
          aria-label=${desc ? `${p.name} — ${desc}` : p.name}
        >
          <div class="well"><ibcs-preview type=${previewType(p.kind, p.category)}></ibcs-preview></div>
          <div class="body">
            <div class="name">${p.name}</div>
            <p class="desc">${desc}</p>
            <div class="crow">
              <span class="v">v${p.latestVersion || "–"}</span>
              <span class="k">${p.kind}</span>
              <span class="v">${formatCount(p.pulls30d)} ${downloadIcon(11)}</span>
            </div>
          </div>
        </a>`;
      })}
    </div>`;
  }

  override render() {
    return html`
      <div class="wrap">
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        <div class="frame">
          <div class="head">
            ${this.scope ? html`<a class="back" href=${`/scopes/${this.scope}`}>← @${this.scope}</a>` : ""}
            <h1>Deleted packages</h1>
            <p>
              ${this.scope
                ? `Archived packages for @${this.scope}. Restore one from its Settings tab.`
                : "Choose a scope to see its deleted packages."}
            </p>
          </div>
          ${this.myRole === undefined
            ? ""
            : roleAtLeast(this.myRole, "maintainer")
              ? this.renderGrid()
              : html`<div class="empty">
                  You don't have access to view deleted packages for this scope.
                </div>`}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-scope-deleted": PageScopeDeleted;
  }
}
